/**
 * Hook for listing previous CSV exports, scoped either to a project or
 * cross-project (global admin view).
 *
 * Auto-refreshes every 5 s while any listed job is still processing, so
 * the UI reflects completion without a manual refresh.
 */

import { useQuery } from '@tanstack/react-query';
import { reportsApi, type ExportJobSummary, type ExportListResponse } from '@/services/reports';

type Scope =
  | { kind: 'project'; projectId: string }
  | { kind: 'global' };

const ACTIVE_REFETCH_MS = 5000;
const IDLE_REFETCH_MS = false as const;

export function useReportExports(scope: Scope, limit = 50) {
  const queryKey =
    scope.kind === 'project'
      ? (['report-exports', 'project', scope.projectId, limit] as const)
      : (['report-exports', 'global', limit] as const);

  const query = useQuery<ExportListResponse>({
    queryKey,
    queryFn: () =>
      scope.kind === 'project'
        ? reportsApi.listProjectExports(scope.projectId, limit)
        : reportsApi.listGlobalExports(limit),
    refetchInterval: (q) => {
      const items = q.state.data?.exports ?? [];
      const hasActive = items.some(
        (e: ExportJobSummary) => e.status === 'processing',
      );
      return hasActive ? ACTIVE_REFETCH_MS : IDLE_REFETCH_MS;
    },
  });

  return {
    exports: query.data?.exports ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    error: query.error,
    refetch: query.refetch,
  };
}
